import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  TextField,
  Stack,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { de } from "date-fns/locale";
import {
  getOpeningOverridesApi,
  upsertOpeningOverrideApi,
  deleteOpeningOverrideApi,
} from "../../Apis/openingOverrides/openingOverrides";
import { getAllSettingsApi } from "../../Apis/settings/settings";
import { useToast } from "../../Hooks/useToast";

const CELL_SIZE = 50;
const DEFAULT_OPEN = "10:00";
const DEFAULT_CLOSE = "22:00";

const formatDateSafe = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toMinutes = (label) => {
  const [hh, mm] = String(label || "").split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const UnavailableTimeSlots = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [overrides, setOverrides] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableRanges, setAvailableRanges] = useState([]);
  const [closedAllDay, setClosedAllDay] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [openingHours, setOpeningHours] = useState({
    open: DEFAULT_OPEN,
    close: DEFAULT_CLOSE,
  });

  const { toastSuccess, toastError } = useToast();

  const loadOverrides = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getOpeningOverridesApi();
      setOverrides(Array.isArray(response.openingOverrides) ? response.openingOverrides : []);
      if (response.error && response.source === "fallback_due_to_error") {
        toastError(response.error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  const loadOpeningHours = useCallback(async () => {
    try {
      const { data } = await getAllSettingsApi();
      const season =
        data?.season === "winter" || data?.season === "summer" ? data.season : "summer";
      const seasonTimings =
        data?.timings?.[season] || data?.timings?.summer || data?.timings?.winter || {};
      const open = /^\d{2}:\d{2}$/.test(seasonTimings?.open || "") ? seasonTimings.open : DEFAULT_OPEN;
      const close = /^\d{2}:\d{2}$/.test(seasonTimings?.close || "")
        ? seasonTimings.close
        : DEFAULT_CLOSE;
      setOpeningHours({ open, close });
    } catch {
      setOpeningHours({ open: DEFAULT_OPEN, close: DEFAULT_CLOSE });
    }
  }, []);

  useEffect(() => {
    loadOverrides();
    loadOpeningHours();
  }, [loadOverrides, loadOpeningHours]);

  const getOverrideForDate = useCallback(
    (dateStr) => overrides.find((o) => o.date === dateStr) || null,
    [overrides]
  );

  const handleDateClick = (date) => {
    const dateStr = formatDateSafe(date);
    const override = getOverrideForDate(dateStr);

    setSelectedDate(dateStr);
    setDialogError("");

    if (override) {
      setClosedAllDay(Boolean(override.closedAllDay));
      const ranges = (override.periods || []).map((p) => ({ from: p.start, to: p.end }));
      setAvailableRanges(
        ranges.length > 0 ? ranges : [{ from: openingHours.open, to: openingHours.close }]
      );
    } else {
      setClosedAllDay(false);
      setAvailableRanges([{ from: openingHours.open, to: openingHours.close }]);
    }

    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isUpdating) return;
    setIsDialogOpen(false);
    setDialogError("");
    setAvailableRanges([]);
    setClosedAllDay(false);
  };

  const handleAddRange = () => {
    setDialogError("");
    setAvailableRanges((prev) => [...prev, { from: openingHours.open, to: openingHours.close }]);
  };

  const handleRemoveRange = (index) => {
    setDialogError("");
    setAvailableRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRangeChange = (index, field, value) => {
    setDialogError("");
    setAvailableRanges((prev) =>
      prev.map((range, i) => (i === index ? { ...range, [field]: value } : range))
    );
  };

  // Standard opening hours are only a reference, NOT a limit: a date override may
  // extend earlier or later. Validation only enforces start<end, no overlap, no dupes.
  const validateRanges = () => {
    const parsed = availableRanges.map((r) => ({ from: toMinutes(r.from), to: toMinutes(r.to) }));

    for (const r of parsed) {
      if (typeof r.from !== "number" || typeof r.to !== "number") {
        return "Bitte gultige Uhrzeiten fur jeden Zeitraum eingeben.";
      }
      if (r.from >= r.to) {
        return "Die Endzeit muss nach der Startzeit liegen.";
      }
    }

    const sorted = [...parsed].sort((a, b) => a.from - b.from);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].from === sorted[i - 1].from && sorted[i].to === sorted[i - 1].to) {
        return "Doppelte Zeitraume sind nicht erlaubt.";
      }
      if (sorted[i].from < sorted[i - 1].to) {
        return "Die Zeitraume durfen sich nicht uberschneiden.";
      }
    }

    return "";
  };

  const handleSave = async () => {
    if (!selectedDate) return;

    let payload;
    if (closedAllDay) {
      payload = { closedAllDay: true, periods: [] };
    } else {
      if (availableRanges.length === 0) {
        setDialogError("Bitte mindestens einen Zeitraum angeben oder 'Ganztagig geschlossen' wahlen.");
        return;
      }
      const validationError = validateRanges();
      if (validationError) {
        setDialogError(validationError);
        return;
      }
      const periods = [...availableRanges]
        .map((r) => ({ start: r.from, end: r.to }))
        .sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
      payload = { closedAllDay: false, periods };
    }

    try {
      setIsUpdating(true);
      setDialogError("");

      const response = await upsertOpeningOverrideApi(selectedDate, payload);
      if (!response.success) {
        const message = response.error || "Die Offnungszeiten konnten nicht gespeichert werden.";
        setDialogError(message);
        toastError(message);
        return;
      }

      await loadOverrides();

      if (closedAllDay) {
        toastSuccess("Tag als ganztagig geschlossen gespeichert.");
      } else {
        toastSuccess("Offnungszeiten fur diesen Tag gespeichert.");
      }
      handleDialogClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveOverride = async (date) => {
    try {
      setIsUpdating(true);
      const response = await deleteOpeningOverrideApi(date);
      if (!response.success) {
        toastError(response.error || "Die Anpassung konnte nicht entfernt werden.");
        return;
      }
      await loadOverrides();
      toastSuccess("Anpassung entfernt - Standardoffnungszeiten gelten wieder.");
    } finally {
      setIsUpdating(false);
    }
  };

  const nextMonth = () => setCurrentMonth(addDays(endOfMonth(currentMonth), 1));
  const prevMonth = () => setCurrentMonth(addDays(startOfMonth(currentMonth), -1));

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <IconButton onClick={prevMonth} disabled={isUpdating}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" fontWeight={600}>
        {format(currentMonth, "MMMM yyyy", { locale: de })}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton onClick={loadOverrides} disabled={isLoading || isUpdating} size="small" title="Daten neu laden">
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={nextMonth} disabled={isUpdating}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }

    return (
      <Grid container spacing={1} justifyContent="flex-start">
        {days.map((dayItem) => {
          if (!isSameMonth(dayItem, monthStart)) return null;

          const override = getOverrideForDate(formatDateSafe(dayItem));
          const hasOverride = Boolean(override);
          const isFullyClosed =
            hasOverride && (override.closedAllDay || (override.periods || []).length === 0);
          const isToday = isSameDay(dayItem, new Date());

          return (
            <Grid item key={dayItem.toString()}>
              <Box
                sx={{
                  position: "relative",
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: "50%",
                  bgcolor: isToday ? "#caa96a" : hasOverride ? "#fff7ed" : "#ffffff",
                  border: hasOverride ? "2px solid #f59e0b" : "1px solid #e0e0e0",
                  color: hasOverride ? "#b45309" : "black",
                  fontWeight: isToday ? "bold" : hasOverride ? "bold" : "normal",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1.1,
                  cursor: isUpdating ? "wait" : "pointer",
                  opacity: isUpdating ? 0.6 : 1,
                  "&:hover": {
                    backgroundColor: isToday ? "#caa96a" : hasOverride ? "#fef3c7" : "#f5f5f5",
                  },
                }}
                title={
                  isFullyClosed
                    ? "Ganztagig geschlossen"
                    : hasOverride
                    ? "Angepasste Offnungszeiten"
                    : "Standardoffnungszeiten"
                }
                onClick={() => !isUpdating && handleDateClick(dayItem)}
              >
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", opacity: 0.7 }}>
                  {format(dayItem, "EEEEEE", { locale: de })}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {format(dayItem, "d")}
                </Typography>
                {hasOverride && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 5,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: isFullyClosed ? "#dc2626" : "#f59e0b",
                    }}
                  />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const monthOverrides = useMemo(
    () =>
      overrides
        .filter((entry) => {
          const parsed = parseISO(entry.date);
          return !Number.isNaN(parsed.getTime()) && isSameMonth(parsed, currentMonth);
        })
        .sort((a, b) => a.date.localeCompare(b.date)),
    [overrides, currentMonth]
  );

  const renderAdjustedList = () => {
    if (monthOverrides.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            Keine angepassten Tage im {format(currentMonth, "MMMM yyyy", { locale: de })}. Alle Tage
            nutzen die Standardoffnungszeiten ({openingHours.open} - {openingHours.close}).
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
            Klicken Sie auf einen Tag im Kalender, um die Offnungszeiten anzupassen.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {monthOverrides.map((entry) => {
            const periods = entry.periods || [];
            const isClosedAllDay = entry.closedAllDay || periods.length === 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={entry.date}>
                <Card
                  sx={{
                    height: "100%",
                    border: "2px solid #f59e0b",
                    backgroundColor: "#fffbeb",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: 3,
                      transform: "translateY(-2px)",
                      transition: "all 0.2s ease-in-out",
                    },
                  }}
                  onClick={() => {
                    if (!isUpdating) handleDateClick(parseISO(entry.date));
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <CalendarTodayIcon sx={{ color: "#b45309", mr: 1, fontSize: "1.2rem" }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} color="#b45309">
                          {format(parseISO(entry.date), "dd.MM.yyyy")}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Angepasste Offnungszeiten
                        </Typography>
                      </Box>
                    </Box>

                    {isClosedAllDay ? (
                      <Chip
                        label="Ganztagig geschlossen"
                        size="small"
                        sx={{ backgroundColor: "#dc2626", color: "white", fontWeight: 600 }}
                      />
                    ) : (
                      <>
                        <Typography variant="caption" fontWeight={600} color="#047857" sx={{ mb: 1, display: "block" }}>
                          Geoffnet:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {periods.map((period, index) => (
                            <Chip
                              key={`${entry.date}-open-${index}`}
                              icon={<AccessTimeIcon sx={{ fontSize: "0.8rem !important" }} />}
                              label={`${period.start} - ${period.end}`}
                              size="small"
                              sx={{
                                backgroundColor: "#10b981",
                                color: "white",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                height: "24px",
                                "& .MuiChip-icon": { color: "white", fontSize: "0.8rem" },
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </CardContent>

                  <CardActions sx={{ pt: 0, pb: 2, px: 2, justifyContent: "space-between" }}>
                    <Typography variant="caption" color="#92400e" sx={{ fontSize: "0.7rem" }}>
                      Zum Bearbeiten klicken
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveOverride(entry.date);
                      }}
                      disabled={isUpdating}
                      title="Anpassung entfernen (Standardzeiten wiederherstellen)"
                      sx={{
                        backgroundColor: "#fef3c7",
                        "&:hover": { backgroundColor: "#fde68a" },
                        width: 28,
                        height: 28,
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {isUpdating && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Lade Offnungszeiten...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" color="black" fontWeight={500} mb={0.5} gutterBottom>
        Offnungszeiten pro Tag verwalten
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: "block" }}>
        Standard: {openingHours.open} - {openingHours.close}. Klicken Sie auf einen Tag, um die
        Offnungszeiten nur fur dieses Datum anzupassen (auch ausserhalb der Standardzeiten moglich).
      </Typography>

      {renderHeader()}
      {renderCalendar()}

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2.5, alignItems: "center", mt: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#f59e0b" }} />
          <Typography variant="caption" color="textSecondary">
            Angepasste Offnungszeiten
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#dc2626" }} />
          <Typography variant="caption" color="textSecondary">
            Ganztagig geschlossen
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" color="black" fontWeight={500} mt={4} gutterBottom>
        Angepasste Tage im {format(currentMonth, "MMMM yyyy", { locale: de })}:
      </Typography>
      {renderAdjustedList()}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Offnungszeiten anpassen
          {selectedDate && (
            <Typography variant="body2" color="textSecondary">
              {format(parseISO(selectedDate), "EEEE, dd.MM.yyyy", { locale: de })} - Standard:{" "}
              {openingHours.open} - {openingHours.close}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={closedAllDay}
                  onChange={(event) => {
                    setClosedAllDay(event.target.checked);
                    setDialogError("");
                  }}
                  color="error"
                />
              }
              label="Ganztagig geschlossen"
            />

            {!closedAllDay && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1.5}>
                  {availableRanges.map((range, index) => (
                    <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                      <TextField
                        label="Von"
                        type="time"
                        value={range.from}
                        onChange={(event) => handleRangeChange(index, "from", event.target.value)}
                        inputProps={{ step: 300 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Bis"
                        type="time"
                        value={range.to}
                        onChange={(event) => handleRangeChange(index, "to", event.target.value)}
                        inputProps={{ step: 300 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveRange(index)}
                        disabled={availableRanges.length <= 1}
                        title="Zeitraum entfernen"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddRange}
                  sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                >
                  Zeitraum hinzufugen
                </Button>
              </>
            )}

            {dialogError && (
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  color: "#b42318",
                  backgroundColor: "#fef3f2",
                  border: "1px solid #fecdca",
                  fontSize: "0.9rem",
                }}
              >
                {dialogError}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isUpdating}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
          >
            {isUpdating ? "Wird gespeichert..." : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnavailableTimeSlots;
